import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Chip, Collapse, Paper, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { messages } from '../../intl';
import { getCurrentExam } from '../../store/model/selectors';
import formatDate, { msToTime } from '../../utils/formatDate';

const SCROLL_THRESHOLD = 10;

const useStyles = makeStyles()((theme) => ({
	eventRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.75),
		flexWrap: 'nowrap',
		overflow: 'hidden',
	},
	deviceName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flexShrink: 1,
		minWidth: 0,
	},
	timestamp: {
		color: theme.palette.text.secondary,
		fontSize: '0.8rem',
		minWidth: '130px',
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	scrollableList: {
		maxHeight: '300px',
		overflowY: 'auto',
		borderRadius: theme.shape.borderRadius,
		border: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(0.5),
	},
	groupedItems: {
		paddingLeft: theme.spacing(3),
		borderLeft: `2px solid ${theme.palette.divider}`,
		marginLeft: theme.spacing(1),
	},
	sectionTitle: {
		fontWeight: 700,
		fontSize: '1rem',
		marginBottom: theme.spacing(0.5),
		display: 'block',
	},
}));

const CLASS_MSG_KEYS = {
	storage: 'peripheralClassStorage',
	video: 'peripheralClassVideo',
	audio: 'peripheralClassAudio',
	keyboard: 'peripheralClassKeyboard',
	mouse: 'peripheralClassMouse',
	hid: 'peripheralClassHid',
	phone: 'peripheralClassPhone',
	bluetooth: 'peripheralClassBluetooth',
	other: 'peripheralClassOther',
};

const ClassChip = ({ deviceClass, intl }) => {
	const labelKey = CLASS_MSG_KEYS[deviceClass] || 'peripheralClassOther';
	return (
		<Chip label={intl.formatMessage(messages[labelKey])} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
	);
};

const ActionChip = ({ action, intl }) => {
	const isConnected = action === 'connected';
	return (
		<Chip
			label={intl.formatMessage(messages[isConnected ? 'peripheralConnected' : 'peripheralDisconnected'])}
			size="small"
			sx={(theme) => ({
				backgroundColor: isConnected ? theme.palette.success.main : theme.palette.warning.main,
				color: 'white',
				fontSize: '0.7rem',
			})}
		/>
	);
};

const getGroupAction = (events) => {
	const actions = new Set(events.map((e) => e.action));
	if (actions.size === 1) return [...actions][0];
	return 'mixed';
};

const processEvents = (events) => {
	const toMs = (t) => (typeof t === 'number' ? t : Date.parse(t));
	const sorted = [...events].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));

	// First pass: collect only events that carry a groupId into a group map.
	// Events without groupId are standalone and never enter this map,
	// so there is no key collision between standalone ids and group ids.
	const groupMap = {};
	for (const event of sorted) {
		if (event.groupId != null) {
			if (!groupMap[event.groupId]) groupMap[event.groupId] = [];
			groupMap[event.groupId].push(event);
		}
	}

	// Second pass: build result in time order.
	const seenGroups = new Set();
	const result = [];

	for (const event of sorted) {
		if (event.groupId != null) {
			if (!seenGroups.has(event.groupId)) {
				seenGroups.add(event.groupId);
				const groupEvents = groupMap[event.groupId];
				if (groupEvents.length === 1) {
					result.push({ type: 'single', event: groupEvents[0] });
				} else {
					result.push({ type: 'group', events: groupEvents, groupId: event.groupId });
				}
			}
		} else {
			// Standalone: no groupId, goes directly as its own row.
			result.push({ type: 'single', event });
		}
	}

	return result;
};

// Same reference the video player uses for its timeline (see seek offsets in ExamPreview/VideoBox).
const getStreamStartedAt = (state) => getCurrentExam(state)?.streamStartedAt;

// Formats an absolute event timestamp (ms) as time elapsed in the exam, e.g. 09:23 or 01:10:30.
// Negative offsets are clamped to 00:00; without a valid reference falls back to the absolute date.
const formatExamTime = (timestampMs, referenceValue) => {
	const referenceMs = typeof referenceValue === 'number' ? referenceValue : Date.parse(referenceValue);
	if (!Number.isFinite(referenceMs) || !Number.isFinite(timestampMs)) return formatDate(timestampMs);
	return msToTime(Math.max(0, timestampMs - referenceMs));
};

const SingleEventRow = ({ event, intl, classes, streamStartedAt }) => {
	return (
		<div className={classes.eventRow} style={{ padding: '8px 0' }}>
			<span className={classes.timestamp}>{formatExamTime(event.timestamp, streamStartedAt)}</span>
			<ActionChip action={event.action} intl={intl} />
			<span className={classes.deviceName}>{event.name}</span>
			<ClassChip deviceClass={event.class} intl={intl} />
		</div>
	);
};

const GroupEventRow = ({ events, intl, classes, streamStartedAt }) => {
	const [open, setOpen] = useState(false);
	const action = getGroupAction(events);
	const firstTimestamp = events[0].timestamp;
	const count = events.length;

	let summaryMsgKey;
	if (action === 'connected') summaryMsgKey = 'peripheralGroupConnected';
	else if (action === 'disconnected') summaryMsgKey = 'peripheralGroupDisconnected';
	else summaryMsgKey = 'peripheralGroupMixed';

	return (
		<>
			<div
				className={classes.eventRow}
				style={{
					padding: '8px 0',
					cursor: 'pointer',
				}}
				onClick={() => setOpen((prev) => !prev)}
			>
				<span className={classes.timestamp}>{formatExamTime(firstTimestamp, streamStartedAt)}</span>
				<span>{intl.formatMessage(messages[summaryMsgKey], { count })}</span>
				{open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
			</div>
			<Collapse in={open} timeout="auto" unmountOnExit>
				<div className={classes.groupedItems}>
					{events.map((e, index) => (
						<SingleEventRow key={e.id ?? index} event={e} intl={intl} classes={classes} streamStartedAt={streamStartedAt} />
					))}
				</div>
			</Collapse>
		</>
	);
};

const Peripherals = ({ peripherals }) => {
	const intl = useIntl();
	const { classes } = useStyles();

	const baseline = useMemo(
		() => (Array.isArray(peripherals?.baseline) ? peripherals.baseline.filter((e) => e != null) : null),
		[peripherals]
	);
	const events = useMemo(
		() => (Array.isArray(peripherals?.events) ? peripherals.events.filter((e) => e != null) : null),
		[peripherals]
	);
	const hasBaseline = !!baseline && baseline.length > 0;
	const hasEvents = !!events && events.length > 0;

	const processedEvents = useMemo(() => (hasEvents ? processEvents(events) : []), [events, hasEvents]);
	const streamStartedAt = useSelector(getStreamStartedAt);

	if (!peripherals) return null;
	if (!hasBaseline && !hasEvents) return null;

	return (
		<>
			{hasBaseline && (
				<Paper elevation={3} style={{ padding: '20px', marginTop: '20px' }}>
					<Typography component="span" className={classes.sectionTitle}>
						<FormattedMessage {...messages.peripheralsAtStart} />
						{` (${baseline.length})`}
					</Typography>
					<div className={baseline.length > SCROLL_THRESHOLD ? classes.scrollableList : undefined}>
						{baseline.map((item, index) => (
							<div key={item.id ?? index} className={classes.eventRow} style={{ padding: '8px 0' }}>
								<span className={classes.deviceName}>{item.name}</span>
								<ClassChip deviceClass={item.class} intl={intl} />
							</div>
						))}
					</div>
				</Paper>
			)}
			{hasEvents && (
				<Paper elevation={3} style={{ padding: '20px', marginTop: '20px' }}>
					<Typography component="span" className={classes.sectionTitle}>
						<FormattedMessage {...messages.peripheralEvents} />
						{` (${events.length})`}
					</Typography>
					<div className={processedEvents.length > SCROLL_THRESHOLD ? classes.scrollableList : undefined}>
						{processedEvents.map((item, index) =>
							item.type === 'single' ? (
								<SingleEventRow key={item.event.id ?? index} event={item.event} intl={intl} classes={classes} streamStartedAt={streamStartedAt} />
							) : (
								<GroupEventRow key={item.groupId ?? index} events={item.events} intl={intl} classes={classes} streamStartedAt={streamStartedAt} />
							)
						)}
					</div>
				</Paper>
			)}
		</>
	);
};

export default Peripherals;
